// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// A catalog cart priced the way checkout prices it (aw-server catalog_pricing.priceCatalogProduct and
// cartDeploymentMinFee): the quote's list extraction and data prices and its discount rows, with the customer's ticked
// layers and data choice on top, which the quote is priced without (EXP-20261020-95, -01). The selector test and
// aw-server's catalog_pricing test assert the same vectors, so the two cannot drift apart.

// One line of a cart as checkout prices it: the list price (the surcharge in, the data out for an own-data cart), its
// surcharge, the tier and promotion shares of the discount, and the total after them, before the minimum fee
interface ICatalogLinePrice {
  listDollar: number;
  surchargeDollar: number;
  tierDiscountDollar: number;
  promotionDollar: number;
  totalDollar: number;
}

// The cart's lines and their prices. `priced` is false for a cart the quote priced as a figure (a contracted bundle
// card, which carries no list prices): its one price is the quote's own figures.
export interface ICatalogCartPrice {
  lines: ICatalogCartLine[];
  priced: boolean;
  linePrices: ICatalogLinePrice[];
}

export const round2 = (n: number): number => Math.round(n * 100) / 100;
export const sumOf = (values: number[]): number => round2(values.reduce((total, value) => total + value, 0));

const hasListPrices = (line: ICatalogCartLine): boolean => typeof line.extractionDollarBeforeDiscount === 'number';

const surchargeApplies = (line: ICatalogCartLine, policy: ISurchargePolicy | null): boolean => (
  !!policy && policy.appliesToSubtypes.includes(line.subtype ?? '')
);

// aw-server catalog_pricing.applyDiscounts: tier rows compound; a promotion floors the total discount at its percent of
// the list price, after the tier rows (it tops a smaller tier discount up and leaves a larger one alone)
const applyDiscountRows = (dollars: number, rows: ICatalogDiscountRow[]): number => round2(rows.reduce((amount, row) => {
  if (!(row.percent > 0)) return amount;
  if (row.source === 'promo') return Math.min(amount, dollars * (1 - row.percent / 100));
  return amount * (1 - row.percent / 100);
}, dollars));

// aw-server catalog_pricing.extraLayerSurcharge: each add-on layer past the allowance adds the policy's rate of the
// extraction's list price, on the subtypes the policy names
const layerSurcharge = (line: ICatalogCartLine, layers: number, policy: ISurchargePolicy | null): number => {
  if (!surchargeApplies(line, policy) || !hasListPrices(line)) return 0;
  const extraLayers = Math.max(0, layers - policy.freeAllowance);
  return Math.round(extraLayers * policy.rate * (line.extractionDollarBeforeDiscount ?? 0) * 100) / 100;
};

// A line priced as aw-server's priceCatalogProduct prices it: the surcharge joins the extraction's list price and is
// discounted with it, and an own-data cart has no data to price
const priceCatalogLine = (line: ICatalogCartLine, layers: number, uploadOwnData: boolean, policy: ISurchargePolicy | null): ICatalogLinePrice => {
  const surchargeDollar = layerSurcharge(line, layers, policy);
  const extractionList = round2((line.extractionDollarBeforeDiscount ?? 0) + surchargeDollar);
  const dataList = uploadOwnData ? 0 : (line.dataDollarBeforeDiscount ?? 0);
  const rows = line.discountRows ?? [];
  const tierRows = rows.filter((row) => row.source !== 'promo');
  const listDollar = round2(extractionList + dataList);
  const tierOnlyDollar = round2(applyDiscountRows(extractionList, tierRows) + applyDiscountRows(dataList, tierRows));
  const totalDollar = round2(applyDiscountRows(extractionList, rows) + applyDiscountRows(dataList, rows));
  return { listDollar, surchargeDollar, tierDiscountDollar: round2(listDollar - tierOnlyDollar), promotionDollar: round2(tierOnlyDollar - totalDollar), totalDollar };
};

// A cart the quote priced as a figure: its own figures
const fixedCatalogPrice = (bundle: IBundle | null, uploadOwnData: boolean): ICatalogLinePrice => ({
  listDollar: Math.max(0, round2((bundle?.priceBeforeDiscountDollar ?? 0) - (uploadOwnData ? (bundle?.dataProviderDollar ?? 0) : 0))),
  surchargeDollar: 0,
  tierDiscountDollar: bundle?.discountDollar ?? 0,
  promotionDollar: bundle?.promotionDollar ?? 0,
  totalDollar: uploadOwnData ? (bundle?.extractionDollar ?? 0) : (bundle?.bundleCost ?? 0),
});

// The lines of a cart: each product of a cart of several (AUT-6630), else the one selection
const cartLines = (bundle: IBundle | null): ICatalogCartLine[] => {
  if ((bundle?.products?.length ?? 0) > 1) return bundle.products;
  return bundle ? [bundle] : [];
};

// The cart priced line by line with `layers` add-on layers ticked and the customer's data choice
export const priceCatalogCart = (bundle: IBundle | null, layers: number, uploadOwnData: boolean, policy: ISurchargePolicy | null): ICatalogCartPrice => {
  const lines = cartLines(bundle);
  const priced = lines.length > 0 && lines.every(hasListPrices);
  return {
    lines,
    priced,
    linePrices: priced ? lines.map((line) => priceCatalogLine(line, layers, uploadOwnData, policy)) : [fixedCatalogPrice(bundle, uploadOwnData)],
  };
};

// The add-on layers past the allowance, as the surcharge row counts them: none when the surcharge applies to no line
export const surchargedLayerCount = (cart: ICatalogCartPrice, layers: number, policy: ISurchargePolicy | null): number => (
  cart.priced && cart.lines.some((line) => surchargeApplies(line, policy)) ? Math.max(0, layers - policy.freeAllowance) : 0
);

// The cart's credits as checkout records them: each line's at the rate, summed (creditAcreage is the rows' credits)
export const cartCredits = (cart: ICatalogCartPrice, dollarPerCredit: number): number => (
  sumOf(cart.linePrices.map((line) => round2(line.totalDollar / dollarPerCredit)))
);

// The minimum project fee floors the floored products' totals together (AUT-6630 D22; aw-server
// catalog_pricing.cartDeploymentMinFee). The quote took its fee on the quoted totals, so the floor is that fee plus them;
// the cart's layers and data choice move the totals, and the fee moves with them as checkout's does. A cart quoted as a
// figure keeps the quote's fee.
export const cartMinimumFeeDollar = (bundle: IBundle | null, cart: ICatalogCartPrice, policy: ISurchargePolicy | null): number => {
  const quotedFeeDollar = bundle?.deploymentFeeDollar ?? 0;
  if (!cart.priced || !(quotedFeeDollar > 0)) return quotedFeeDollar;
  const floored = cart.lines.length > 1 ? (bundle?.deploymentFeeProducts ?? []) : cart.lines.map((line) => line.name);
  const flooredIndexes = cart.lines.map((line, index) => (floored.includes(line.name) ? index : -1)).filter((index) => index >= 0);
  const floorDollar = round2(quotedFeeDollar + sumOf(flooredIndexes.map((index) => priceCatalogLine(cart.lines[index], 0, false, policy).totalDollar)));
  const pricedDollar = sumOf(flooredIndexes.map((index) => cart.linePrices[index].totalDollar));
  return pricedDollar < floorDollar ? round2(floorDollar - pricedDollar) : 0;
};

// The breakdown's line items for a cart of several products (Joel, 2026-10-19): each product's list figure without the
// surcharge, its data out when the customer uploads their own data, so the lines sum to the subtotal
export const cartLineItems = (cart: ICatalogCartPrice, uploadOwnData: boolean): { _id: string; name: string; dollar: number }[] => (
  cart.lines.map((line, index) => ({
    _id: line._id,
    name: line.name,
    dollar: cart.priced
      ? Math.max(0, round2(cart.linePrices[index].listDollar - cart.linePrices[index].surchargeDollar))
      : Math.max(0, round2((line.priceBeforeDiscountDollar ?? 0) - (uploadOwnData ? (line.dataProviderDollar ?? 0) : 0))),
  }))
);
